<template>
  <ContentModal v-model="show" title="Admin Reports — Your Scheduling Autopilot" size="2xl">
    <div class="range-bar mb-3">
      <div class="range-presets">
        <button v-for="p in presets" :key="p.days" type="button" class="btn btn-sm mb-0"
          :class="activePreset === p.days ? 'btn-success' : 'btn-outline-secondary'" @click="applyPreset(p.days)">
          {{ p.label }}
        </button>
      </div>
      <div class="range-inputs">
        <label>From <input type="date" v-model="startDate" :min="minDate" :max="endDate || maxDate" class="form-control form-control-sm" /></label>
        <label>To <input type="date" v-model="endDate" :min="startDate || minDate" :max="maxDate" class="form-control form-control-sm" /></label>
        <button type="button" class="btn btn-sm btn-success mb-0" :disabled="loading || !rangeValid" @click="reload">Apply</button>
      </div>
      <div v-if="rangeMessage" class="range-msg text-danger">{{ rangeMessage }}</div>
      <div v-else class="range-msg text-secondary">Choose up to 1 year of history.</div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-success" role="status"><span class="visually-hidden">Loading...</span></div>
      <p class="mt-2 text-muted">Gathering what the platform has done for you...</p>
    </div>

    <div v-else-if="error" class="text-center py-4 text-danger">
      <i class="material-icons" style="font-size:48px;">error_outline</i>
      <p class="mt-2">{{ error }}</p>
    </div>

    <div v-else-if="stats" class="admin-reports">
      <!-- Hero -->
      <div class="hero mb-4">
        <div class="hero-orbs"><span></span><span></span><span></span></div>
        <div class="row align-items-center position-relative">
          <div class="col-lg-8">
            <div class="hero-badge"><i class="material-icons">auto_awesome</i> Autopilot</div>
            <h3 class="hero-title">
              Your platform handled
              <span class="hero-number"><AnimatedNumber :value="totalActions" /></span>
              scheduling tasks for you
            </h3>
            <p class="hero-sub">
              {{ stats.period?.label }} · {{ stats.location_name }} · {{ scopeLabel }}
            </p>
          </div>
          <div class="col-lg-4 text-lg-end mt-3 mt-lg-0">
            <div class="hero-time">
              <i class="material-icons">schedule</i>
              <div>
                <div class="hero-time-value"><AnimatedNumber :value="stats.time_saved?.hours || 0" :decimals="1" /> hrs</div>
                <div class="hero-time-label">of admin time saved (estimate)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Headline tiles -->
      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="tile tile-green">
            <div class="tile-icon"><i class="material-icons">event_available</i></div>
            <div class="tile-value"><AnimatedNumber :value="stats.auto_scheduled?.total || 0" /></div>
            <div class="tile-label">Positions auto-scheduled</div>
            <div class="tile-foot">
              <AnimatedNumber :value="stats.auto_scheduled?.confirmed || 0" /> confirmed ·
              {{ stats.auto_scheduled?.confirm_rate || 0 }}% confirm rate
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="tile tile-blue">
            <div class="tile-icon"><i class="material-icons">published_with_changes</i></div>
            <div class="tile-value"><AnimatedNumber :value="stats.substitutes?.total || 0" /></div>
            <div class="tile-label">Substitutes auto-processed</div>
            <div class="tile-foot">
              <AnimatedNumber :value="stats.substitutes?.confirmed || 0" /> confirmed ·
              {{ stats.substitutes?.confirm_rate || 0 }}% confirm rate
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="tile tile-purple">
            <div class="tile-icon"><i class="material-icons">swap_horiz</i></div>
            <div class="tile-value"><AnimatedNumber :value="stats.swaps?.sent || 0" /></div>
            <div class="tile-label">Swap requests sent</div>
            <div class="tile-foot">
              <AnimatedNumber :value="stats.swaps?.responded || 0" /> responded ·
              {{ stats.swaps?.response_rate || 0 }}% response rate
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-4">
        <!-- Substitute pipeline -->
        <div class="col-lg-6">
          <div class="panel h-100">
            <h6 class="panel-title"><i class="material-icons">published_with_changes</i> Substitute autopilot</h6>
            <p class="panel-sub">When a volunteer can't make it, the platform finds and notifies a replacement.</p>
            <div class="pipeline">
              <div class="pipe-step"><i class="material-icons">person_off</i><span>Volunteer steps out</span></div>
              <div class="pipe-line"><i></i><i></i><i></i></div>
              <div class="pipe-step pipe-bot"><i class="material-icons">smart_toy</i><span>Platform finds a sub</span></div>
              <div class="pipe-line"><i></i><i></i><i></i></div>
              <div class="pipe-step pipe-done"><i class="material-icons">how_to_reg</i><span>Sub confirms</span></div>
            </div>
            <div class="d-flex align-items-center justify-content-around mt-3 flex-wrap">
              <ProgressRing :percent="stats.substitutes?.confirm_rate || 0" :size="110" color="#0ea5e9" />
              <div class="legend-list">
                <div><span class="dot bg-confirm"></span><AnimatedNumber :value="stats.substitutes?.confirmed || 0" /> confirmed</div>
                <div><span class="dot bg-pending"></span><AnimatedNumber :value="stats.substitutes?.pending || 0" /> awaiting reply</div>
                <div><span class="dot bg-decline"></span><AnimatedNumber :value="stats.substitutes?.declined || 0" /> declined</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Swap flow -->
        <div class="col-lg-6">
          <div class="panel h-100">
            <h6 class="panel-title"><i class="material-icons">swap_horiz</i> Volunteer swap flexibility</h6>
            <p class="panel-sub">Volunteers trade assignments themselves, so you don't have to step in.</p>
            <div class="swap-steps">
              <div class="swap-sent"><AnimatedNumber :value="stats.swaps?.sent || 0" /><small>sent</small></div>
              <i class="material-icons arrow">arrow_forward</i>
              <div class="swap-sent"><AnimatedNumber :value="stats.swaps?.responded || 0" /><small>responded</small></div>
              <i class="material-icons arrow">arrow_forward</i>
              <div class="swap-split">
                <span class="yes"><i class="material-icons">thumb_up</i><AnimatedNumber :value="stats.swaps?.accepted || 0" /> yes</span>
                <span class="no"><i class="material-icons">thumb_down</i><AnimatedNumber :value="stats.swaps?.declined || 0" /> no</span>
              </div>
            </div>
            <div class="stack-bar mt-3">
              <div class="seg seg-yes" :style="{ width: swapPct.yes + '%' }"></div>
              <div class="seg seg-no" :style="{ width: swapPct.no + '%' }"></div>
              <div class="seg seg-wait" :style="{ width: swapPct.wait + '%' }"></div>
            </div>
            <div class="stack-legend">
              <span><span class="dot bg-confirm"></span>Yes</span>
              <span><span class="dot bg-decline"></span>No</span>
              <span><span class="dot bg-pending"></span>Waiting ({{ stats.swaps?.pending || 0 }})</span>
            </div>
            <p class="flex-note" v-if="(stats.swaps?.accepted || 0) > 0">
              <i class="material-icons">favorite</i>
              {{ stats.swaps.accepted }} swap{{ stats.swaps.accepted === 1 ? '' : 's' }} completed between volunteers —
              flexibility that kept your teams covered.
            </p>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-lg-8">
          <div class="panel h-100">
            <h6 class="panel-title"><i class="material-icons">insights</i> Activity over time</h6>
            <p class="panel-sub">Everything the platform did, {{ stats.period?.granularity === 'day' ? 'day by day' : 'month by month' }}.</p>
            <div class="chart-wrap"><canvas ref="trendCanvas"></canvas></div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="panel h-100 text-center">
            <h6 class="panel-title justify-content-center"><i class="material-icons">groups</i> Next 30 days</h6>
            <p class="panel-sub">How fully staffed your upcoming schedule is.</p>
            <ProgressRing :percent="stats.coverage?.percent || 0" :size="150" :stroke="14" color="#22c55e">
              <div>
                <div class="ring-big"><AnimatedNumber :value="stats.coverage?.percent || 0" :decimals="0" />%</div>
                <div class="ring-small">filled</div>
              </div>
            </ProgressRing>
            <p class="mt-3 mb-0 text-sm text-secondary">
              {{ stats.coverage?.filled || 0 }} of {{ stats.coverage?.needed || 0 }} positions covered
            </p>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-lg-6">
          <div class="panel h-100">
            <h6 class="panel-title"><i class="material-icons">leaderboard</i> Where autopilot helped most</h6>
            <div v-if="stats.top_activities?.length">
              <div v-for="row in stats.top_activities" :key="row.name" class="bar-row">
                <div class="bar-label">{{ row.name }}</div>
                <div class="bar-track"><div class="bar-fill" :style="{ width: barWidth(row.count) + '%' }"></div></div>
                <div class="bar-count">{{ row.count }}</div>
              </div>
            </div>
            <p v-else class="text-secondary mb-0">No automated activity yet in this period.</p>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="panel h-100">
            <h6 class="panel-title"><i class="material-icons">bolt</i> Behind the scenes</h6>
            <div class="facts">
              <div class="fact"><strong><AnimatedNumber :value="stats.executions || 0" /></strong><span>autopilot runs</span></div>
              <div class="fact"><strong><AnimatedNumber :value="stats.swaps?.acceptance_rate || 0" :decimals="0" />%</strong><span>of swap replies were yes</span></div>
              <div class="fact"><strong><AnimatedNumber :value="stats.auto_scheduled?.declined || 0" /></strong><span>auto-scheduled declines (autopilot refills)</span></div>
            </div>
            <p class="text-xs text-secondary mt-3 mb-0">
              Time saved is an estimate: about 3 min per auto-scheduled position, 10 min per auto-processed
              substitute and 5 min per answered swap. Figures reflect only the areas you administer.
            </p>
          </div>
        </div>
      </div>
    </div>
  </ContentModal>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Chart from 'chart.js/auto'
import ContentModal from '@/components/Modal/ContentModal.vue'
import AnimatedNumber from '@/components/AdminReports/AnimatedNumber.vue'
import ProgressRing from '@/components/AdminReports/ProgressRing.vue'
import api from '@/config/api'

const props = defineProps({ modelValue: Boolean, locationId: [Number, String] })
const emit = defineEmits(['update:modelValue'])
const show = computed({ get: () => props.modelValue, set: (value) => emit('update:modelValue', value) })

const stats = ref(null)
const loading = ref(false)
const error = ref('')
const trendCanvas = ref(null)
let chart = null

const MAX_DAYS = 365
const presets = [
  { days: 7, label: '7 days' },
  { days: 31, label: '31 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
]
const toISO = (d) => {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d }
const maxDate = toISO(new Date())
const minDate = toISO(daysAgo(MAX_DAYS - 1))
// Default: last 31 days (today included)
const startDate = ref(toISO(daysAgo(30)))
const endDate = ref(maxDate)

const spanDays = computed(() => {
  if (!startDate.value || !endDate.value) return 0
  return Math.round((new Date(endDate.value) - new Date(startDate.value)) / 86400000) + 1
})
const activePreset = computed(() => {
  const p = presets.find((x) => x.days === spanDays.value)
  return p && endDate.value === maxDate ? p.days : null
})
const rangeMessage = computed(() => {
  if (!startDate.value || !endDate.value) return 'Choose a start and end date.'
  if (spanDays.value < 1) return 'The start date must be before the end date.'
  if (spanDays.value > MAX_DAYS) return 'The range cannot exceed 1 year.'
  return ''
})
const rangeValid = computed(() => !rangeMessage.value)

function applyPreset(days) {
  endDate.value = maxDate
  startDate.value = toISO(daysAgo(days - 1))
  reload()
}

const totalActions = computed(() =>
  (stats.value?.auto_scheduled?.total || 0) + (stats.value?.substitutes?.total || 0) + (stats.value?.swaps?.responded || 0)
)

const scopeLabel = computed(() => ({
  all: 'All areas',
  activity: 'Your activities',
  workgroup: 'Your workgroups',
  instance: 'Your events',
}[stats.value?.scope] || 'Your areas'))

const swapPct = computed(() => {
  const s = stats.value?.swaps
  const sent = s?.sent || 0
  if (!sent) return { yes: 0, no: 0, wait: 0 }
  return { yes: (s.accepted / sent) * 100, no: (s.declined / sent) * 100, wait: (s.pending / sent) * 100 }
})

const maxTop = computed(() => Math.max(1, ...(stats.value?.top_activities || []).map((r) => r.count)))
const barWidth = (count) => Math.max(4, (count / maxTop.value) * 100)

function destroyChart() { if (chart) { chart.destroy(); chart = null } }

function renderChart() {
  destroyChart()
  const trend = stats.value?.trend || []
  if (!trendCanvas.value || !trend.length) return
  chart = new Chart(trendCanvas.value, {
    type: 'bar',
    data: {
      labels: trend.map((t) => t.label),
      datasets: [
        { label: 'Auto-scheduled', data: trend.map((t) => t.auto_scheduled), backgroundColor: '#22c55e', borderRadius: 6 },
        { label: 'Substitutes', data: trend.map((t) => t.substitutes), backgroundColor: '#0ea5e9', borderRadius: 6 },
        { label: 'Swap requests', data: trend.map((t) => t.swaps), backgroundColor: '#8b5cf6', borderRadius: 6 },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 1400, easing: 'easeOutQuart' },
      plugins: { legend: { position: 'bottom' } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    },
  })
}

async function fetchStats() {
  if (!props.locationId) return
  loading.value = true
  error.value = ''
  try {
    stats.value = (await api.get(`/locations/${props.locationId}/admin-reports-stats`, {
      params: { start: startDate.value, end: endDate.value },
    })).data?.data || null
  } catch (exception) {
    error.value = exception.response?.data?.message || 'Failed to load admin reports.'
  } finally {
    loading.value = false
  }
}

async function reload() {
  if (!rangeValid.value) return
  await fetchStats()
  await nextTick()
  renderChart()
}

watch(show, async (open) => {
  if (!open) { destroyChart(); return }
  await reload()
}, { immediate: true })

onBeforeUnmount(destroyChart)
</script>

<style scoped>
.range-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; background: #f1f5f9; border-radius: 12px; padding: 10px 14px; }
.range-presets { display: flex; flex-wrap: wrap; gap: 6px; }
.range-inputs { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.range-inputs label { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; margin: 0; }
.range-inputs input { width: 150px; }
.range-msg { flex-basis: 100%; font-size: 0.75rem; }
.admin-reports { color: #1e293b; }

.hero {
  position: relative; overflow: hidden; border-radius: 16px; padding: 28px;
  background: linear-gradient(120deg, #0f172a 0%, #1e3a8a 55%, #0e7490 100%); color: #fff;
}
.hero-orbs span {
  position: absolute; border-radius: 50%; filter: blur(30px); opacity: 0.45;
  animation: float 8s ease-in-out infinite;
}
.hero-orbs span:nth-child(1) { width: 160px; height: 160px; background: #22c55e; top: -40px; left: 10%; }
.hero-orbs span:nth-child(2) { width: 200px; height: 200px; background: #8b5cf6; bottom: -80px; right: 15%; animation-delay: -3s; }
.hero-orbs span:nth-child(3) { width: 120px; height: 120px; background: #0ea5e9; top: 30%; right: 40%; animation-delay: -5s; }
@keyframes float { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(18px) scale(1.12); } }
.hero-badge {
  display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.15);
  padding: 4px 12px; border-radius: 999px; font-size: 0.8rem; font-weight: 600; margin-bottom: 10px;
}
.hero-badge .material-icons { font-size: 16px; animation: spin-slow 6s linear infinite; }
@keyframes spin-slow { to { transform: rotate(360deg); } }
.hero-title { color: #fff; font-weight: 700; margin-bottom: 6px; line-height: 1.3; }
.hero-number { font-size: 2.6rem; color: #86efac; padding: 0 6px; }
.hero-sub { color: rgba(255,255,255,0.8); margin: 0; }
.hero-time {
  display: inline-flex; align-items: center; gap: 12px; background: rgba(255,255,255,0.12);
  border-radius: 14px; padding: 14px 18px; backdrop-filter: blur(4px);
}
.hero-time .material-icons { font-size: 36px; color: #fde68a; animation: tick 2s ease-in-out infinite; }
@keyframes tick { 0%, 100% { transform: rotate(-8deg); } 50% { transform: rotate(8deg); } }
.hero-time-value { font-size: 1.6rem; font-weight: 700; text-align: left; }
.hero-time-label { font-size: 0.75rem; opacity: 0.85; text-align: left; }

.tile {
  border-radius: 16px; padding: 20px; color: #fff; position: relative; overflow: hidden; height: 100%;
  animation: rise 0.6s ease both; transition: transform 0.2s;
}
.tile:hover { transform: translateY(-4px); }
.tile-green { background: linear-gradient(135deg, #16a34a, #4ade80); }
.tile-blue { background: linear-gradient(135deg, #0369a1, #38bdf8); }
.tile-purple { background: linear-gradient(135deg, #6d28d9, #a78bfa); }
.tile-icon { position: absolute; right: 14px; top: 10px; opacity: 0.25; }
.tile-icon .material-icons { font-size: 64px; }
.tile-value { font-size: 2.6rem; font-weight: 800; line-height: 1; }
.tile-label { font-weight: 600; margin-top: 4px; }
.tile-foot { font-size: 0.8rem; opacity: 0.9; margin-top: 8px; }
@keyframes rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px; }
.panel-title { display: flex; align-items: center; gap: 8px; font-weight: 700; margin-bottom: 2px; }
.panel-title .material-icons { color: #0ea5e9; font-size: 20px; }
.panel-sub { color: #64748b; font-size: 0.85rem; margin-bottom: 14px; }

.pipeline { display: flex; align-items: center; justify-content: center; gap: 6px; flex-wrap: wrap; }
.pipe-step {
  display: flex; flex-direction: column; align-items: center; gap: 4px; font-size: 0.72rem; text-align: center;
  background: #f1f5f9; border-radius: 12px; padding: 10px; width: 96px;
}
.pipe-step .material-icons { font-size: 26px; color: #64748b; }
.pipe-bot { background: #e0f2fe; }
.pipe-bot .material-icons { color: #0284c7; animation: pulse 1.6s ease-in-out infinite; }
.pipe-done { background: #dcfce7; }
.pipe-done .material-icons { color: #16a34a; }
@keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.25); } }
.pipe-line { display: flex; gap: 4px; }
.pipe-line i { width: 6px; height: 6px; border-radius: 50%; background: #38bdf8; animation: flow 1.2s infinite; }
.pipe-line i:nth-child(2) { animation-delay: 0.2s; }
.pipe-line i:nth-child(3) { animation-delay: 0.4s; }
@keyframes flow { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }

.legend-list { display: flex; flex-direction: column; gap: 6px; font-size: 0.9rem; }
.dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 6px; }
.bg-confirm { background: #22c55e; }
.bg-pending { background: #f59e0b; }
.bg-decline { background: #ef4444; }

.swap-steps { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; }
.swap-sent {
  display: flex; flex-direction: column; align-items: center; background: #f5f3ff; border-radius: 12px;
  padding: 10px 16px; font-size: 1.6rem; font-weight: 800; color: #6d28d9; min-width: 90px;
}
.swap-sent small { font-size: 0.72rem; font-weight: 500; color: #64748b; }
.arrow { color: #a78bfa; animation: nudge 1.4s ease-in-out infinite; }
@keyframes nudge { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(5px); } }
.swap-split { display: flex; flex-direction: column; gap: 6px; font-weight: 700; }
.swap-split span { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 999px; }
.swap-split .yes { background: #dcfce7; color: #15803d; }
.swap-split .no { background: #fee2e2; color: #b91c1c; }
.swap-split .material-icons { font-size: 16px; }

.stack-bar { display: flex; height: 14px; border-radius: 999px; overflow: hidden; background: #e2e8f0; }
.seg { height: 100%; transition: width 1.4s cubic-bezier(0.22, 1, 0.36, 1); }
.seg-yes { background: #22c55e; }
.seg-no { background: #ef4444; }
.seg-wait { background: #f59e0b; }
.stack-legend { display: flex; gap: 14px; font-size: 0.8rem; margin-top: 6px; color: #475569; }
.flex-note {
  display: flex; gap: 8px; align-items: center; margin: 14px 0 0; padding: 10px 12px; font-size: 0.85rem;
  background: #fdf2f8; border-radius: 10px; color: #9d174d;
}
.flex-note .material-icons { font-size: 18px; animation: pulse 1.6s ease-in-out infinite; }

.chart-wrap { position: relative; height: 280px; }
.ring-big { font-size: 1.7rem; font-weight: 800; line-height: 1; }
.ring-small { font-size: 0.75rem; color: #64748b; font-weight: 500; }

.bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.bar-label { width: 38%; font-size: 0.85rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bar-track { flex: 1; height: 12px; background: #e2e8f0; border-radius: 999px; overflow: hidden; }
.bar-fill { height: 100%; background: linear-gradient(90deg, #22c55e, #0ea5e9); border-radius: 999px; animation: grow 1.2s ease both; }
@keyframes grow { from { transform: scaleX(0); transform-origin: left; } to { transform: scaleX(1); transform-origin: left; } }
.bar-count { width: 32px; text-align: right; font-weight: 700; font-size: 0.85rem; }

.facts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.fact { background: #f8fafc; border-radius: 12px; padding: 12px; text-align: center; }
.fact strong { display: block; font-size: 1.5rem; color: #0f172a; }
.fact span { font-size: 0.75rem; color: #64748b; }

@media (max-width: 620px) {
  .facts { grid-template-columns: 1fr; }
  .hero-number { font-size: 2rem; }
}
@media (prefers-reduced-motion: reduce) {
  * { animation: none !important; transition: none !important; }
}
</style>
